import { useFeaturedTrips, type FeaturedTrip, type FeaturedTripCreator } from "components/b2c/shared/useFeaturedTrips";
import Icon from "components/Icons/Icon";
import trackEvent from "components/tracking/trackEvent";
import ProfilePicture from "components/user/ProfilePicture";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { t } from "i18n/index";
import { useMarketingLang } from "i18n/useMarketingLang";
import { lazy, Suspense, useEffect, useRef, useState } from "react";

import ReelPoster from "../img/reel-poster.png";
import { getHomeTranslations } from "../marketing/i18n/pages/home";

// react-modal is only needed once a trip is actually played — lazy so it (and its CSS-in-JS
// body-lock wiring) never ships in the homepage's critical chunk for a reader who never plays one.
const HighlightedTripModal = lazy(() => import("components/b2c/shared/HighlightedTripModal"));

const ux = (id: string, w: number, h: number) => `https://images.unsplash.com/photo-${id}?crop=faces&fit=crop&auto=format&q=80&w=${w}&h=${h}`;

// Decorative avatar + flag scatter above the headline — placeholder stock photos, not real users.
// Positions mirror the approved Figma design 1:1 (top/left/tilt/size in px, relative to the copy column).
const AVATAR_SCATTER = [
	{ type: "user", photo: "1548213238-0da7521bd6e0", top: -24, left: 210, tilt: 7, size: 52 },
	{ type: "flag", flag: "jp", top: 18, left: 276, tilt: -5, size: 28 },
	{ type: "user", photo: "1637692513738-68ed61f6604a", top: -12, left: 330, tilt: 9, size: 44 },
	{ type: "flag", flag: "ma", top: 26, left: 388, tilt: 6, size: 24 },
	{ type: "user", photo: "1612000649051-611809293050", top: -20, left: 420, tilt: -6, size: 48 },
	{ type: "flag", flag: "no", top: 10, left: 482, tilt: 8, size: 26 },
	{ type: "user", photo: "1578955682343-97ea7f7464da", top: -8, left: 460, tilt: 4, size: 36 },
] as const;

const STAT_TILES = [
	{ key: "travelers", value: "1.5M+", title: "travelers worldwide", sub: "and growing every day", modifier: "green" },
	{ key: "trips", value: "2M+", title: "trips shared", sub: "across the world", modifier: "orange" },
	{ key: "rating", value: "4.7", title: "App Store rating", sub: "across 8,000+ ratings", modifier: "pink" },
	{ key: "free", value: "free", title: "always free to browse", sub: "no account needed", modifier: "purple" },
];

const getCreator = (item: FeaturedTrip) => item?.creator;

const getCreatorAvatar = (creator: FeaturedTripCreator) => creator?.pictureUrl;

const getPosterUrl = (item: FeaturedTrip) => {
	return item?.posterUrl || ReelPoster;
};

const getTripUrl = (item: FeaturedTrip, creator: FeaturedTripCreator) => {
	if (!creator?.username || !item?.name) return "/discover";

	return `/${creator.username}/trip/${encodeURIComponent(item.name)}`;
};

const CommunityTripCard = ({
	item,
	onPlay,
	translations,
}: {
	item: FeaturedTrip;
	onPlay: (item: FeaturedTrip) => void;
	translations: Record<string, string>;
}) => {
	const creator = getCreator(item);
	const tripUrl = getTripUrl(item, creator);

	return (
		<div className="community-trip-card">
			<img src={getPosterUrl(item)} alt={item?.name || ""} loading="lazy" className="community-trip-card__photo" />
			<div className="community-trip-card__scrim" />

			<button type="button" className="community-trip-card__play" aria-label={`Play ${item?.name || "trip"} reel`} onClick={() => onPlay(item)}>
				<Icon name="playTriangle" size={22} fill="#ffffff" />
			</button>

			<span className="community-trip-card__badge">{item?.name}</span>

			<div className="community-trip-card__footer">
				<p className="community-trip-card__name">{item?.name}</p>
				<div className="community-trip-card__meta">
					<div className="community-trip-card__author">
						<ProfilePicture image={getCreatorAvatar(creator)} name={creator?.name} />
						<span>{creator?.name}</span>
					</div>
					<a href={tripUrl} className="community-trip-card__view">
						{t(translations, "home.community.viewTrip", "View trip")}
					</a>
				</div>
			</div>
		</div>
	);
};

const CommunitySection = () => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const scrollYBeforePlayRef = useRef(0);
	const { trips } = useFeaturedTrips();
	const [playingTrip, setPlayingTrip] = useState<FeaturedTrip | null>(null);
	// Set once, the first time playingTrip becomes truthy, and never back to false — the modal
	// (and react-modal itself) mounts from then on so its close animation keeps working on every
	// subsequent open/close, not just the first.
	const [modalRequested, setModalRequested] = useState(false);
	useEffect(() => {
		if (playingTrip && !modalRequested) setModalRequested(true);
	}, [playingTrip, modalRequested]);
	const lang = useMarketingLang();
	const translations = getHomeTranslations(lang);
	// Both CTAs below used to be <a href="/discover">, the dead browse index. They are buttons
	// now rather than anchors kept alive by preventDefault: an href is still reachable through
	// middle-click, "open in new tab" and crawlers, and there is no page worth sending those to
	// — /discover is dead and /register is the pre-redesign form. No href, no way back to either.
	const gate = usePlanGate("homepage_community", "store");

	const scroll = (dir: "left" | "right") => {
		const container = scrollRef.current;
		if (!container) return;
		// A fixed pixel step overshoots on narrow (mobile) viewports, skipping past
		// the very next card instead of bringing it into view — step by exactly
		// one card's width (+ gap) so "next"/"previous" always reveals one item.
		const card = container.querySelector<HTMLElement>(".community-trip-card");
		const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
		const step = card ? card.offsetWidth + gap : 700;
		container.scrollBy({ left: dir === "right" ? step : -step, behavior: "smooth" });
	};

	const handlePlay = (item: FeaturedTrip) => {
		trackEvent("click_play_button", { trip: { name: item?.name, _id: item?.tripId } }, "marketing_site");
		// react-modal's mobile body-lock (position: fixed, see General.scss `.ReactModal__Body--open`)
		// resets the page's scroll offset — restore it below once the modal closes.
		scrollYBeforePlayRef.current = window.scrollY;
		setPlayingTrip(item);
	};

	const handleCloseModal = () => {
		const restoreY = scrollYBeforePlayRef.current;
		const pathAtClose = window.location.pathname;
		setPlayingTrip(null);
		requestAnimationFrame(() => {
			if (window.location.pathname === pathAtClose) window.scrollTo(0, restoreY);
		});
	};

	return (
		<section className="community-section">
			<div className="community-section__inner community-section__intro-wrap">
				<div className="community-section__intro">
					<div className="community-section__copy">
						<div className="community-section__avatars">
							{AVATAR_SCATTER.map((a) =>
								a.type === "user" ? (
									<span
										key={a.photo}
										className="community-avatar"
										style={{ width: a.size, height: a.size, top: a.top, left: a.left, transform: `rotate(${a.tilt}deg)` }}
									>
										<img src={ux(a.photo, 120, 120)} alt="" loading="lazy" />
									</span>
								) : (
									<span
										key={a.flag}
										className="community-avatar community-avatar--flag"
										style={{ width: a.size, height: a.size, top: a.top, left: a.left, transform: `rotate(${a.tilt}deg)` }}
									>
										<img src={`https://flagcdn.com/w80/${a.flag}.png`} alt="" loading="lazy" />
									</span>
								)
							)}
						</div>

						<p className="community-section__eyebrow">{t(translations, "home.community.eyebrow", "Community trips")}</p>
						<h2>
							{t(translations, "home.community.headingLine1", "Over 2 million trips.")}
							<br />
							<span className="community-section__accent">{t(translations, "home.community.headingAccent", "All waiting to inspire you.")}</span>
						</h2>
						<p className="community-section__sub">
							{t(
								translations,
								"home.community.description",
								"Stippl is home to the world's largest community of trip planners. Browse real itineraries from real travelers, and make them your own."
							)}
						</p>
						<button type="button" className="community-section__link" onClick={gate.requestPlan}>
							{t(translations, "home.community.exploreLink", "Explore the community →")}
						</button>
					</div>

					<div className="community-section__stats">
						{STAT_TILES.map((tile) => (
							<div className={`stat-tile stat-tile--${tile.modifier}`} key={tile.key}>
								{/* Through `t()` like the two lines below it. Three of the four tiles hold a
								    number, which needs no translating, and "free" inherited that treatment —
								    so a Dutch visitor read "free" above "altijd gratis om te bekijken". */}
								<strong>{t(translations, `home.community.stats.${tile.key}.value`, tile.value)}</strong>
								<p>{t(translations, `home.community.stats.${tile.key}.title`, tile.title)}</p>
								<span>{t(translations, `home.community.stats.${tile.key}.sub`, tile.sub)}</span>
							</div>
						))}
					</div>
				</div>
			</div>

			{!!trips?.length ? (
				<div className="community-section__carousel" ref={scrollRef}>
					{trips.map((item) => (
						<CommunityTripCard key={item?.id} item={item} onPlay={handlePlay} translations={translations} />
					))}
				</div>
			) : (
				<div className="community-section__carousel" style={{ minHeight: "30rem" }} />
			)}

			<div className="community-section__inner community-section__footer-wrap">
				<div className="community-section__footer">
					<div className="community-section__nav">
						<button type="button" aria-label="Previous trips" onClick={() => scroll("left")}>
							<Icon name="lineArrowLeft" size={16} fill="#0f3e4a" />
						</button>
						<button type="button" aria-label="Next trips" onClick={() => scroll("right")}>
							<Icon name="lineArrowRight" size={16} fill="#0f3e4a" />
						</button>
					</div>
					<button type="button" className="community-section__browse" onClick={gate.requestPlan}>
						{t(translations, "home.community.browseAll", "Browse all trips")}
						<Icon name="chevron-right" size={8} fill="#0f3e4a" />
					</button>
				</div>
			</div>

			{modalRequested && (
				<Suspense fallback={null}>
					<HighlightedTripModal trip={playingTrip} isOpen={!!playingTrip} onClose={handleCloseModal} />
				</Suspense>
			)}
			<PlanGateModal
				gate={gate}
				title={t(translations, "home.auth.community", "Create a free account to browse community trips")}
				loginTitle={t(translations, "home.auth.login", "Log in to plan with AI")}
			/>
		</section>
	);
};

export default CommunitySection;
